<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';

const WARN_PCT = 80;
const HIGH_PCT = 90;
const BLOCKED_PCT = 100;

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountId } = useAccount();
const getAccount = useMapGetter('accounts/getAccount');

const quota = computed(() => {
  const account = getAccount.value(accountId.value) || {};
  return account.whatsapp_quota || {};
});

const contactsPct = computed(() => {
  const current = quota.value;
  const limit = Number(current.plan_contacts || 0);
  if (!limit) return 0;
  if (current.contacts_ratio != null) return Number(current.contacts_ratio);
  return Math.min((Number(current.unique_contacts || 0) / limit) * 100, 100);
});

const messagesPct = computed(() => {
  const current = quota.value;
  if (current.unlimited) return 0;
  return Number(current.usage_ratio || 0);
});

const contactsOver = computed(() => contactsPct.value >= WARN_PCT);
const messagesOver = computed(() => messagesPct.value >= WARN_PCT);
const blocked = computed(() => messagesPct.value >= BLOCKED_PCT);

const isAlert = computed(
  () =>
    blocked.value ||
    contactsPct.value >= HIGH_PCT ||
    messagesPct.value >= HIGH_PCT
);

// Account + cycle + severity, so dismissing 80% does not hide a later 90%/100%, another
// account, or the same severity reached again after a billing-cycle rollover in a long-lived
// SPA session (dismissedFor is in-memory only, so it would otherwise survive the rollover).
const severityTier = computed(() => {
  if (blocked.value) return 'blocked';
  if (isAlert.value) return 'critical';
  return 'warning';
});
const dismissalKey = computed(
  () => `${accountId.value}:${quota.value.cycle_start}:${severityTier.value}`
);
const dismissedFor = ref(null);
const dismissed = computed(() => dismissedFor.value === dismissalKey.value);

const shouldShow = computed(() => {
  if (!isAdmin.value || dismissed.value) return false;
  const current = quota.value;
  if (!current.enforcement_enabled) return false;
  if (current.configured === false) return false;
  if (
    !current.configured &&
    current.plan_contacts == null &&
    current.message_quota == null
  ) {
    return false;
  }
  return contactsOver.value || messagesOver.value;
});

const formatPct = value => {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const contactsRemaining = computed(() => {
  const limit = Number(quota.value.plan_contacts || 0);
  if (!limit) return null;
  return Math.max(limit - Number(quota.value.unique_contacts || 0), 0);
});

const messagesRemaining = computed(() => {
  if (quota.value.remaining_messages == null) return null;
  return Math.max(Number(quota.value.remaining_messages), 0);
});

const remainingCopy = (count, oneKey, manyKey) => {
  if (count == null || count <= 0) return '';
  if (count === 1) return t(oneKey);
  return t(manyKey, { count });
};

const bannerMessage = computed(() => {
  const parts = [];
  if (blocked.value) parts.push(t('PLAN_USAGE_BANNER.BLOCKED_NOTE'));

  if (contactsOver.value && messagesOver.value) {
    parts.push(
      t(
        blocked.value
          ? 'PLAN_USAGE_BANNER.REACHED_BOTH'
          : 'PLAN_USAGE_BANNER.BOTH',
        {
          contacts: formatPct(contactsPct.value),
          messages: formatPct(messagesPct.value),
        }
      )
    );
  } else if (messagesOver.value) {
    const sentence = t(
      blocked.value
        ? 'PLAN_USAGE_BANNER.REACHED_MESSAGES'
        : 'PLAN_USAGE_BANNER.MESSAGES',
      { pct: formatPct(messagesPct.value) }
    );
    const remaining = blocked.value
      ? ''
      : remainingCopy(
          messagesRemaining.value,
          'PLAN_USAGE_BANNER.MESSAGES_REMAINING_ONE',
          'PLAN_USAGE_BANNER.MESSAGES_REMAINING'
        );
    parts.push([sentence, remaining].filter(Boolean).join(' '));
  } else if (contactsOver.value) {
    const sentence = t('PLAN_USAGE_BANNER.CONTACTS', {
      pct: formatPct(contactsPct.value),
    });
    const remaining = remainingCopy(
      contactsRemaining.value,
      'PLAN_USAGE_BANNER.CONTACTS_REMAINING_ONE',
      'PLAN_USAGE_BANNER.CONTACTS_REMAINING'
    );
    parts.push([sentence, remaining].filter(Boolean).join(' '));
  }

  return parts.join(' ');
});

const openPlanUsage = () => {
  router.push({ name: 'plan_usage_reports' });
};

const dismiss = () => {
  dismissedFor.value = dismissalKey.value;
};
</script>

<template>
  <div
    v-if="shouldShow"
    class="relative flex min-h-10 items-center justify-center px-12 py-1.5"
    :class="
      isAlert ? 'bg-n-ruby-2 text-n-ruby-12' : 'bg-n-amber-2 text-n-amber-12'
    "
    role="status"
  >
    <div class="flex min-w-0 items-center gap-2">
      <span
        class="block size-3.5 shrink-0"
        :class="isAlert ? 'i-lucide-info' : 'i-lucide-triangle-alert'"
      />
      <p class="!m-0 min-w-0 !text-sm !font-medium !leading-5">
        {{ bannerMessage }}
      </p>
      <button
        type="button"
        class="!m-0 !inline-flex !h-6 !shrink-0 !items-center !gap-1 !rounded-md !px-2 !py-0 !text-sm !font-medium !leading-none"
        :class="
          isAlert
            ? '!border-0 !bg-n-ruby-9 !text-white hover:!bg-n-ruby-10'
            : '!border !border-solid !border-n-slate-7 !bg-white !text-n-slate-12 hover:!bg-n-slate-2 dark:!bg-n-solid-2'
        "
        @click="openPlanUsage"
      >
        {{ $t('PLAN_USAGE_BANNER.ACTION') }}
        <span class="i-lucide-arrow-right block size-3.5" />
      </button>
    </div>
    <button
      type="button"
      class="absolute right-3 top-1/2 !m-0 !inline-flex !size-6 !-translate-y-1/2 !items-center !justify-center !border-0 !bg-transparent !p-0 !text-n-slate-12"
      :aria-label="$t('PLAN_USAGE_BANNER.DISMISS')"
      @click="dismiss"
    >
      <span class="i-lucide-x block size-4" />
    </button>
  </div>
</template>
